import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Wordmark from "@/components/Wordmark";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";

/** Landing page for the single-use confirmation link. On success the session is issued and the
 *  user continues exactly where registration left off. */
export default function VerifyEmail() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [state, setState] = useState("checking");
  const [error, setError] = useState("");
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;              // the link is single use — never POST it twice
    ran.current = true;
    const token = params.get("token");
    if (!token) { setState("error"); setError("This confirmation link is incomplete."); return; }
    api.post("/auth/verify-email", { token })
      .then(({ data }) => {
        setUser(data.user);
        setState("done");
        setTimeout(() => navigate(data.user.role === "cleaner" ? "/cleaner/onboarding"
          : ["admin", "super_admin"].includes(data.user.role) ? "/admin" : "/customer",
          { replace: true }), 1600);
      })
      .catch((e) => { setState("error"); setError(formatApiError(e.response?.data?.detail)); });
  }, [params, navigate, setUser]);

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <header className="glass border-b border-primary/5 h-[72px] flex items-center px-5 sm:px-8">
        <Link to="/"><Wordmark iconClass="h-9 w-auto" textClass="text-lg sm:text-xl" /></Link>
      </header>
      <div className="flex-1 flex items-start justify-center px-5 py-16">
        <div className="w-full max-w-md text-center" data-testid="verify-email-page">
          {state === "checking" && (
            <>
              <Loader2 className="h-10 w-10 animate-spin text-accent mx-auto" />
              <p className="mt-4 text-muted-foreground">Confirming your email address…</p>
            </>
          )}
          {state === "done" && (
            <>
              <CheckCircle2 className="h-12 w-12 text-emerald-600 mx-auto" />
              <h1 className="font-display text-3xl font-semibold mt-4" data-testid="verify-success">Email confirmed ✓</h1>
              <p className="text-muted-foreground mt-2">Taking you to your account…</p>
            </>
          )}
          {state === "error" && (
            <>
              <XCircle className="h-12 w-12 text-destructive mx-auto" />
              <h1 className="font-display text-2xl font-semibold mt-4" data-testid="verify-error">We couldn't confirm that link</h1>
              <p className="text-muted-foreground mt-2">{error}</p>
              <p className="text-sm text-muted-foreground mt-3">
                Log in again and we'll send you a fresh confirmation link straight away. Your
                account and any details you've already entered are safe.
              </p>
              <Link to="/login" className="inline-block mt-5 px-6 py-3 rounded-full bg-accent text-white font-medium" data-testid="verify-back-to-login">Back to log in</Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
